/** Shared cart state helpers — optimistic updates + server merge. */

export const getCartProductId = (item) => item?.itemId ?? item?.productId;

/** Unique key for a cart row: regular and offer lines for the same SKU stay separate. */
export const getCartLineKey = (item) => {
  const productId = String(item?.itemId ?? item?.productId ?? '');
  if (item?.isOfferItem === true) {
    return `offer:${productId}:${item?.offerId ?? ''}`;
  }
  return `regular:${productId}`;
};

export const isSameCartLine = (a, b) => getCartLineKey(a) === getCartLineKey(b);

/**
 * Catalog qty lookups use productId → only index non-offer lines so offer qty
 * does not overwrite / inflate regular product card quantities.
 */
export const buildCartObjFromItems = (items) =>
  (items ?? []).reduce((acc, item) => {
    if (item?.isOfferItem === true) {
      return acc;
    }
    const id = getCartProductId(item);
    if (id != null && id !== '') {
      acc[id] = item;
    }
    return acc;
  }, {});

export const cloneCartSnapshot = (cartItems) => JSON.parse(JSON.stringify(cartItems ?? {}));

/** Preserve visible row order when applying a server cart snapshot. */
export const mergeServerCartItems = (serverItems, previousItems) => {
  if (!Array.isArray(serverItems)) {
    return [];
  }
  if (!previousItems?.length) {
    return serverItems;
  }

  const serverByKey = new Map(
    serverItems.map((item) => [getCartLineKey(item), item]),
  );
  const merged = [];
  const seen = new Set();

  for (const prev of previousItems) {
    const key = getCartLineKey(prev);
    if (serverByKey.has(key)) {
      merged.push(serverByKey.get(key));
      seen.add(key);
    }
  }

  for (const item of serverItems) {
    const key = getCartLineKey(item);
    if (!seen.has(key)) {
      merged.push(item);
    }
  }

  return merged;
};

export const resolveCartLineItem = (cartItems, item, index) => {
  const lineKey = getCartLineKey(item);
  if (!Number.isNaN(index) && index >= 0 && index < (cartItems?.items?.length ?? 0)) {
    const atIndex = cartItems.items[index];
    if (isSameCartLine(atIndex, item)) {
      return atIndex;
    }
  }

  if (item?.isOfferItem !== true) {
    const productId = getCartProductId(item);
    const fromCartObj = cartItems?.cartobj?.[productId];
    if (fromCartObj && fromCartObj?.isOfferItem !== true) {
      return fromCartObj;
    }
  }

  const fromItems = cartItems?.items?.find((dataItem) => getCartLineKey(dataItem) === lineKey);
  return fromItems ?? item;
};

export const buildCartWithUpdatedItem = (cartItems, item, index) => {
  const productId = getCartProductId(item);
  const existingItems = cartItems?.items ?? [];
  const items = [...existingItems];
  let resolvedIndex = index;

  if (!Number.isNaN(resolvedIndex)) {
    const atIndex = existingItems[resolvedIndex];
    if (!atIndex || !isSameCartLine(atIndex, item)) {
      resolvedIndex = NaN;
    }
  }

  if (Number.isNaN(resolvedIndex)) {
    resolvedIndex = items.findIndex((dataItem) => isSameCartLine(dataItem, item));
    if (resolvedIndex !== -1) {
      items[resolvedIndex] = item;
    } else {
      items.push(item);
    }
  } else {
    items[resolvedIndex] = item;
  }

  const cartobj = { ...(cartItems?.cartobj ?? {}) };
  if (item?.isOfferItem !== true && productId != null && productId !== '') {
    cartobj[productId] = item;
  }

  return {
    ...cartItems,
    billDetails: undefined,
    cartobj,
    items,
  };
};

export const buildCartWithRemovedItem = (cartItems, item, index) => {
  const productId = getCartProductId(item);
  const cartobj = { ...(cartItems?.cartobj ?? {}) };
  if (item?.isOfferItem !== true && productId != null) {
    delete cartobj[productId];
  }

  const existingItems = cartItems?.items ?? [];
  let resolvedIndex = index;
  if (!Number.isNaN(resolvedIndex)) {
    const atIndex = existingItems[resolvedIndex];
    if (!atIndex || !isSameCartLine(atIndex, item)) {
      resolvedIndex = NaN;
    }
  }
  if (Number.isNaN(resolvedIndex)) {
    resolvedIndex = existingItems.findIndex((dataItem) => isSameCartLine(dataItem, item));
  }

  const items =
    resolvedIndex >= 0 && resolvedIndex < existingItems.length
      ? [
          ...existingItems.slice(0, resolvedIndex),
          ...existingItems.slice(resolvedIndex + 1),
        ]
      : existingItems.filter((dataItem) => !isSameCartLine(dataItem, item));

  return {
    ...cartItems,
    billDetails: undefined,
    cartobj,
    items,
  };
};
